import type { ReactNode } from 'react';
import { styled } from 'styled-components';

import { Box } from 'components/Box/Box';
import { Text } from 'components/Text/Text';
import { TabContext } from 'containers/TabContext';
import { colors, transition } from 'theme/theme';

const Input = styled.input<{ $isTabbing?: boolean }>`
  width: 100%;
  height: 100%;
  margin: 0;
  appearance: none;
  cursor: pointer;
  ${(props) =>
    !props.$isTabbing &&
    `
    &:focus {
      outline: 0;
    }
  `}
`;
const SquareRadioButtonContainer = styled.div<{
  $checked?: boolean;
  $disabled?: boolean;
}>`
  ${(props) => {
    if (props.$disabled && props.$checked) {
      return `
        --container-bg-color: ${colors.selectionBlue};
        --container-text-color: ${colors.mediumContentGrey};
        --container-opacity: 0.5;
      `;
    }

    if (props.$disabled) {
      return `
        --container-bg-color: ${colors.inputGrey};
        --container-text-color: ${colors.lightContentGrey};
        --container-opacity: 0.5;
      `;
    }

    if (props.$checked) {
      return `
        --container-bg-color: ${colors.selectionBlue};
        --container-text-color: ${colors.mediumContentGrey};
        --container-opacity: 1;
      `;
    }

    return `
        --container-bg-color: ${colors.inputGrey};
        --container-text-color: ${colors.lightContentGrey};
        --container-opacity: 1;
    `;
  }}
  position: relative;
  display: flex;
  align-items: center;
  padding: 8px 16px;
  height: 100%;
  border-radius: 10px;
  transition:
    background ${transition},
    color ${transition},
    opacity 0.5s;

  background: var(--container-bg-color);
  color: var(--container-text-color);
  opacity: var(--container-opacity, 1);

  &:hover {
    --container-bg-color: color-mix(
      in sRGB,
      ${colors.selectionBlue} 60%,
      ${colors.white}
    );
  }

  input[type='radio'] {
    border-radius: 10px;
  }

  ${(props) =>
    props.$disabled
      ? ` &, * { pointer-events: none; } `
      : ` * { cursor: pointer; }
  `}
`;

type Props = {
  id?: string;
  name?: string;
  value: string | boolean | number;
  label: ReactNode;
  checked: boolean;
  onChange?: (newValue: string | boolean | number) => void;
  onClick?: () => void;
  disabled?: boolean;
  required?: boolean;
  'data-qa-id'?: string;
};

export function SquareRadioButton({
  id,
  name,
  value,
  label,
  checked,
  disabled,
  required,
  'data-qa-id': qaId,
  onChange,
  onClick,
}: Props) {
  return (
    <SquareRadioButtonContainer $checked={checked} $disabled={disabled}>
      <Box top={0} left={0} position="absolute" width={1} height="100%">
        <TabContext.Consumer>
          {(isTabbing) => (
            <Input
              type="radio"
              name={name}
              id={id}
              checked={checked}
              required={required}
              disabled={disabled}
              value={typeof value === 'string' ? value : value.toString()}
              onChange={(e) => {
                e.stopPropagation();

                if (onChange) {
                  onChange(value);
                }
              }}
              onClick={onClick}
              data-qa-id={qaId}
              $isTabbing={isTabbing}
            />
          )}
        </TabContext.Consumer>
      </Box>
      <label htmlFor={id}>
        <Text styleAs="h4">{label}</Text>
      </label>
    </SquareRadioButtonContainer>
  );
}
